import React from "react";
import { Link as HeadlessLink } from "react-aria-components";

import { Text } from "../../Text";
import type { LinkProps } from "./types";
import styles from "./styles.module.css";

/**
 * Schemes that execute code or smuggle documents when used as an href.
 * Hrefs are frequently binding-controlled upstream (Markdown links, table
 * URL cells, the Link widget), so the primitive itself refuses to render
 * them as links.
 */
const UNSAFE_SCHEMES = /^(javascript|data|vbscript):/;

/**
 * Browsers ignore C0 control chars (TAB/LF/CR, …) and DEL mid-URL, so they
 * can hide a dangerous scheme (e.g. "java<TAB>script:"). Strip them before
 * checking. Whitespace is also collapsed so "java script:" can't dodge the
 * scheme check.
 */
function isUnsafeHref(href: string): boolean {
  const collapsed = Array.from(href)
    .filter((char) => {
      const code = char.charCodeAt(0);

      return code > 0x1f && code !== 0x7f;
    })
    .join("")
    .replace(/\s+/g, "")
    .toLowerCase();

  return UNSAFE_SCHEMES.test(collapsed);
}

/** target="_blank" without noopener leaks window.opener (tab-nabbing). */
function hardenRel(rel: string | undefined, target: string | undefined) {
  if (target !== "_blank") return rel;

  const tokens = new Set((rel ?? "").split(/\s+/).filter(Boolean));

  tokens.add("noopener");
  tokens.add("noreferrer");

  return [...tokens].join(" ");
}

export function Link(props: LinkProps) {
  const {
    children,
    download,
    href,
    ping,
    referrerPolicy,
    rel,
    target,
    ...rest
  } = props;

  // Unsafe scheme → render the children as plain text. No anchor, no
  // role="link", not focusable — nothing announces or acts like a link.
  if (Boolean(href) && isUnsafeHref(href as string)) {
    return <Text {...rest}>{children}</Text>;
  }

  return (
    <HeadlessLink
      className={styles.link}
      download={download}
      href={href}
      ping={ping}
      referrerPolicy={referrerPolicy}
      rel={hardenRel(rel, target)}
      target={target}
    >
      <Text color="accent" {...rest}>
        {children}
      </Text>
    </HeadlessLink>
  );
}
